import { useLanguage } from '../context/LanguageContext';
import React, { useState, useCallback, useEffect } from 'react';

// ─── Konstanten ───────────────────────────────────────────────────────────────

// ELEMENT_TYPE_LABELS: wird im Component mit t() befüllt (siehe useElementTypeLabels)
// COLLECTION_TYPE_LABELS: wird im Component mit t() befüllt

const CONFLICT_STRATEGIES = [
    { value: 'skip',      label: '⏭ Skip',      desc: 'Bereits vorhandene überspringen' },
    { value: 'update',    label: '🔄 Update',    desc: 'Nur überschreiben wenn Paket neuer ist' },
    { value: 'overwrite', label: '♻ Overwrite',  desc: 'Immer überschreiben (ignoriert Versionscheck)' },
    { value: 'rename',    label: '✏ Rename',     desc: 'Als Kopie mit Suffix importieren' },
];

// ─── LibraryTab ───────────────────────────────────────────────────────────────

const LibraryTab = ({ toast, allClients, makeAuthenticatedRequest, isSuperAdmin }) => {

    // Eigene client_id aus localStorage (für group=3 genutzt)
    const myClientId = (() => {
        try { return parseInt(JSON.parse(localStorage.getItem('hejvi_user') || '{}').client_id || '0'); }
        catch { return 0; }
    })();

    // ── Gemeinsam ─────────────────────────────────────────────────────────────
    const { t } = useLanguage();
    const ELEMENT_TYPE_LABELS    = {
      0: t('lib_type_video') || 'Video',
      1: t('lib_type_audio') || 'Audio',
      2: t('lib_type_photo') || 'Foto',
      9: t('lib_type_question') || 'Frage',
      10: t('lib_type_tutorial') || 'Tutorial',
    };
    const COLLECTION_TYPE_LABELS = {
      0: t('lib_col_tutorial')  || 'Tutorial',
      1: t('lib_col_explainer') || 'Explainer',
      2: t('lib_col_system')    || 'System',
      3: t('lib_col_challenge') || 'Challenge',
      4: t('lib_col_course')    || 'Kurs',
    };
    const CONFLICT_STRATEGIES_T = [
      { value: 'skip',      label: '⏭ Skip',     desc: t('lib_cs_skip')      || 'Bereits vorhandene überspringen' },
      { value: 'update',    label: '🔄 Update',   desc: t('lib_cs_update')    || 'Nur überschreiben wenn Paket neuer ist' },
      { value: 'overwrite', label: '♻ Overwrite', desc: t('lib_cs_overwrite') || 'Immer überschreiben (ignoriert Versionscheck)' },
      { value: 'rename',    label: '✏ Rename',    desc: t('lib_cs_rename')    || 'Als Kopie mit Suffix importieren' },
    ];
    const [activeSection, setActiveSection] = useState('export');

    // ── Export-State ──────────────────────────────────────────────────────────
    const [sourceClientId,   setSourceClientId]   = useState(isSuperAdmin ? '' : String(myClientId));
    const [previewData,      setPreviewData]      = useState(null);
    const [previewLoading,   setPreviewLoading]   = useState(false);
    const [selectedColls,    setSelectedColls]    = useState([]);
    const [selectedElems,    setSelectedElems]    = useState([]);
    const [packageName,      setPackageName]      = useState('');
    const [packageVersion,   setPackageVersion]   = useState('1.0');
    const [exportLoading,    setExportLoading]    = useState(false);

    // ── Import-State ──────────────────────────────────────────────────────────
    const [importFile,        setImportFile]       = useState(null);
    const [importData,        setImportData]       = useState(null);
    const [targetClientId,    setTargetClientId]   = useState(isSuperAdmin ? '' : String(myClientId));
    const [conflictStrategy,  setConflictStrategy] = useState('update');
    const [importLoading,     setImportLoading]    = useState(false);
    const [importResult,      setImportResult]     = useState(null);

    // Für group=3: source automatisch laden wenn gemountet
    useEffect(() => {
        if (!isSuperAdmin && myClientId && !previewData) {
            setSourceClientId(String(myClientId));
        }
    }, [isSuperAdmin, myClientId]); // eslint-disable-line

    // ── Export: Preview laden ─────────────────────────────────────────────────

    const loadPreview = useCallback(async () => {
        const srcId = sourceClientId || (isSuperAdmin ? '' : String(myClientId));
        if (!srcId) return;
        setPreviewLoading(true);
        setPreviewData(null);
        setSelectedColls([]);
        setSelectedElems([]);
        try {
            const res = await makeAuthenticatedRequest(
                `/admin/library/preview?source_client_id=${srcId}`
            );
            if (res?.error) throw new Error(res.error);
            setPreviewData(res);
        } catch (e) {
            toast?.((t('adm_load_error') || 'Fehler beim Laden') + ': ' + e.message, 'error');
        } finally {
            setPreviewLoading(false);
        }
    }, [sourceClientId, isSuperAdmin, myClientId, makeAuthenticatedRequest, toast]);

    // ── Export: Auswahl-Toggles ───────────────────────────────────────────────

    const toggleColl = useCallback((id) =>
        setSelectedColls(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]),
    []);

    const toggleElem = useCallback((id) =>
        setSelectedElems(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]),
    []);

    const toggleAllColls = useCallback(() => {
        if (!previewData) return;
        const all = previewData.collections.map(c => c.id);
        setSelectedColls(prev => prev.length === all.length ? [] : all);
    }, [previewData]);

    const toggleAllElems = useCallback(() => {
        if (!previewData) return;
        const all = previewData.elements.map(e => e.id);
        setSelectedElems(prev => prev.length === all.length ? [] : all);
    }, [previewData]);

    // ── Export: Download ──────────────────────────────────────────────────────

    const handleExport = useCallback(async () => {
        if (selectedColls.length === 0 && selectedElems.length === 0) {
            toast?.(t('lib_select_one') || 'Bitte mindestens ein Element oder eine Collection auswählen.', 'warn');
            return;
        }
        setExportLoading(true);
        try {
            const res = await makeAuthenticatedRequest('/admin/library/export', {
                method: 'POST',
                body: JSON.stringify({
                    source_client_id: parseInt(sourceClientId || myClientId),
                    collection_ids:   selectedColls,
                    element_ids:      selectedElems,
                    package_name:     packageName || 'export',
                    package_version:  packageVersion || '1.0',
                }),
            });
            if (res?.error) throw new Error(res.error);

            const blob     = new Blob([JSON.stringify(res, null, 2)], { type: 'application/json' });
            const url      = URL.createObjectURL(blob);
            const a        = document.createElement('a');
            const safeName = (packageName || 'export').replace(/[^a-z0-9_-]/gi, '_');
            const safeVer  = (packageVersion || '1.0').replace(/[^a-z0-9.]/gi, '_');
            a.href         = url;
            a.download     = `${safeName}_v${safeVer}.hejvi`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            const s = res.stats || {};
            toast?.(
                `Export: ${s.collections ?? 0} Collections, ${s.elements ?? 0} Elemente, ` +
                `${s.chunks ?? 0} Chunks, ${s.file_storage ?? 0} Dateien`,
                'success'
            );
        } catch (e) {
            toast?.((t('lib_export_failed') || 'Export fehlgeschlagen') + ': ' + e.message, 'error');
        } finally {
            setExportLoading(false);
        }
    }, [sourceClientId, myClientId, selectedColls, selectedElems, packageName, packageVersion, makeAuthenticatedRequest, toast]);

    // ── Import: Datei einlesen ────────────────────────────────────────────────

    const handleFileSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImportFile(file);
        setImportData(null);
        setImportResult(null);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const parsed = JSON.parse(ev.target.result);
                if (parsed.hejvi_export_version !== '1.0') {
                    toast?.(t('lib_invalid_file') || 'Ungültige .hejvi-Datei (Version 1.0 erwartet)', 'error');
                    setImportFile(null);
                    return;
                }
                setImportData(parsed);
                // Paketname automatisch aus Datei vorbelegen
                if (parsed.library_package && !packageName) {
                    setPackageName(parsed.library_package);
                }
            } catch {
                toast?.(t('lib_invalid_json') || 'Datei ist kein gültiges JSON', 'error');
                setImportFile(null);
            }
        };
        reader.readAsText(file, 'UTF-8');
    }, [packageName, toast]);

    // ── Import: Ausführen ─────────────────────────────────────────────────────

    const handleImport = useCallback(async () => {
        if (!importData || (!isSuperAdmin && !myClientId) || (isSuperAdmin && !targetClientId)) {
            toast?.(t('lib_file_client_required') || 'Datei und Ziel-Mandant erforderlich.', 'warn');
            return;
        }
        setImportLoading(true);
        setImportResult(null);
        let res = null;
        try {
            res = await makeAuthenticatedRequest('/admin/library/import', {
                method: 'POST',
                body: JSON.stringify({
                    target_client_id:  parseInt(targetClientId || myClientId),
                    conflict_strategy: conflictStrategy,
                    data:              importData,
                }),
            });
            if (res?.error) throw new Error(res.error);
            setImportResult(res);

            if (res.version_status === 'up_to_date') {
                toast?.(`Paket v${res.export_version} ist bereits installiert — nichts zu tun.`, 'info');
            } else if (res.version_status === 'downgrade_blocked') {
                toast?.(`Import blockiert: installierte Version v${res.installed_version} ist neuer als v${res.export_version}.`, 'warn');
            } else {
                const s = res.stats || {};
                toast?.(
                    `Import: ${s.elements_imported ?? 0} Elemente, ${s.collections_imported ?? 0} Collections, ` +
                    `${s.files_copied ?? 0} Dateien kopiert`,
                    'success'
                );
            }
        } catch (e) {
            // Partial result anzeigen wenn vorhanden
            if (res && typeof res === 'object') setImportResult(res);
            toast?.((t('lib_import_failed') || 'Import') + ': ' + e.message, 'error');
        } finally {
            setImportLoading(false);
        }
    }, [importData, targetClientId, myClientId, isSuperAdmin, conflictStrategy, makeAuthenticatedRequest, toast]);

    // ── Installierte Versionen für Import-Ziel laden ──────────────────────────

    const [installedPackagesForTarget, setInstalledPackagesForTarget] = useState({});

    useEffect(() => {
        const tgt = targetClientId || (isSuperAdmin ? '' : String(myClientId));
        if (!tgt) return;
        makeAuthenticatedRequest(`/admin/library/preview?source_client_id=${tgt}`)
            .then(res => setInstalledPackagesForTarget(res?.installed_packages ?? {}))
            .catch(() => {});
    }, [targetClientId, isSuperAdmin, myClientId]); // eslint-disable-line

    // ── Render-Hilfsfunktionen ────────────────────────────────────────────────

    const clientOptions = (allClients || []).map(c => (
        <option key={c.id} value={c.id}>{c.name} (id={c.id})</option>
    ));

    const installedVersionForPackage = importData?.library_package
        ? installedPackagesForTarget[importData.library_package]
        : null;

    const versionStatusBadge = () => {
        if (!importData?.library_package || !importData?.library_version) return null;
        const pkg = importData.library_package;
        const ver = importData.library_version;
        const installed = installedPackagesForTarget[pkg];
        if (!installed) return (
            <span className="lib-version-badge lib-version-badge--new">
                Erstinstallation
            </span>
        );
        const cmp = compareVersions(installed, ver);
        if (cmp < 0)  return <span className="lib-version-badge lib-version-badge--update">Update v{installed} → v{ver}</span>;
        if (cmp === 0) return <span className="lib-version-badge lib-version-badge--current" title="Nutze 'Overwrite' um trotzdem zu reimportieren">v{ver} bereits installiert ↓ Overwrite wählen</span>;
        return <span className="lib-version-badge lib-version-badge--downgrade">⚠ Downgrade v{installed} → v{ver}</span>;
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="adm-tab-content">

            {/* Sektion-Switcher */}
            <div className="adm-subtabs" style={{ marginBottom: '1.5rem' }}>
                <button className={`adm-subtab ${activeSection === 'export' ? 'adm-subtab--active' : ''}`}
                    onClick={() => setActiveSection('export')}>📦 Export</button>
                <button className={`adm-subtab ${activeSection === 'import' ? 'adm-subtab--active' : ''}`}
                    onClick={() => setActiveSection('import')}>📥 Import</button>
            </div>

            {/* ── EXPORT ──────────────────────────────────────────────────── */}
            {activeSection === 'export' && (
                <div className="lib-section">
                    <h3 className="adm-section-title">{t('lib_export_title') || 'Paket exportieren'}</h3>

                    <div className="lib-row lib-row--meta">
                        <div className="lib-field">
                            <label className="adm-label">{t('lib_package_name') || 'Paketname'}</label>
                            <input className="adm-input" placeholder="z.B. hejvi_tutorials"
                                value={packageName} onChange={e => setPackageName(e.target.value)} />
                        </div>
                        <div className="lib-field lib-field--narrow">
                            <label className="adm-label">{t('lib_version') || 'Version'}</label>
                            <input className="adm-input" placeholder="1.0"
                                value={packageVersion} onChange={e => setPackageVersion(e.target.value)} />
                        </div>
                    </div>

                    {/* Quell-Mandant: nur für SuperAdmin wählbar */}
                    {isSuperAdmin ? (
                        <div className="lib-row">
                            <div className="lib-field">
                                <label className="adm-label">{t('lib_source_client') || 'Quell-Mandant'}</label>
                                <select className="adm-input" value={sourceClientId}
                                    onChange={e => { setSourceClientId(e.target.value); setPreviewData(null); setSelectedColls([]); setSelectedElems([]); }}>
                                    <option value="">— Mandant wählen —</option>
                                    {clientOptions}
                                </select>
                            </div>
                            <div className="lib-field lib-field--action">
                                <label className="adm-label">&nbsp;</label>
                                <button className="adm-btn adm-btn--secondary" onClick={loadPreview}
                                    disabled={!sourceClientId || previewLoading}>
                                    {previewLoading ? '⏳ Lade…' : '🔍 Inhalte anzeigen'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="lib-row">
                            <div className="lib-field lib-field--action">
                                <label className="adm-label">&nbsp;</label>
                                <button className="adm-btn adm-btn--secondary" onClick={loadPreview}
                                    disabled={previewLoading}>
                                    {previewLoading ? '⏳ Lade…' : '🔍 Eigene Inhalte laden'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Installierte Pakete (nur wenn previewData geladen) */}
                    {previewData?.installed_packages && Object.keys(previewData.installed_packages).length > 0 && (
                        <div className="lib-installed-packages">
                            <span className="lib-installed-packages__label">{t('lib_installed_packages') || 'Installierte Pakete:'}</span>
                            {Object.entries(previewData.installed_packages).map(([pkg, ver]) => (
                                <span key={pkg} className="lib-version-badge lib-version-badge--current">
                                    {pkg} v{ver}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Preview: Collections */}
                    {previewData && (
                        <div className="lib-preview">
                            <div className="lib-preview__section">
                                <div className="lib-preview__header">
                                    <span className="lib-preview__title">📚 Collections ({previewData.collections.length})</span>
                                    <button className="adm-btn adm-btn--xs" onClick={toggleAllColls}>
                                        {selectedColls.length === previewData.collections.length && previewData.collections.length > 0
                                            ? (t('adm_deselect_all') || 'Alle abwählen') : (t('adm_select_all') || 'Alle wählen')}
                                    </button>
                                    {selectedColls.length > 0 && <span className="lib-badge">{selectedColls.length} ausgewählt</span>}
                                </div>
                                {previewData.collections.length === 0
                                    ? <p className="adm-table__empty">{t('lib_no_collections') || 'Keine Collections'}</p>
                                    : (
                                        <div className="lib-checklist">
                                            {previewData.collections.map(c => (
                                                <label key={c.id} className={`lib-checkitem ${selectedColls.includes(c.id) ? 'lib-checkitem--selected' : ''}`}>
                                                    <input type="checkbox" checked={selectedColls.includes(c.id)}
                                                        onChange={() => toggleColl(c.id)} />
                                                    <span className="lib-checkitem__name">{c.name || '—'}</span>
                                                    <span className="lib-checkitem__meta">
                                                        {COLLECTION_TYPE_LABELS[c.type] ?? c.type}
                                                        {' · '}{c.element_count} Elemente
                                                        {c.status !== 1 && ' · ⏸'}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )
                                }
                            </div>

                            {/* Preview: Elemente */}
                            <div className="lib-preview__section" style={{ marginTop: '1.5rem' }}>
                                <div className="lib-preview__header">
                                    <span className="lib-preview__title">🎬 Einzelne Elemente ({previewData.elements.length})</span>
                                    <button className="adm-btn adm-btn--xs" onClick={toggleAllElems}>
                                        {selectedElems.length === previewData.elements.length && previewData.elements.length > 0
                                            ? 'Alle abwählen' : 'Alle wählen'}
                                    </button>
                                    {selectedElems.length > 0 && <span className="lib-badge">{selectedElems.length} ausgewählt</span>}
                                </div>
                                <p className="lib-hint">Elemente in ausgewählten Collections werden automatisch mitexportiert.</p>
                                {previewData.elements.length === 0
                                    ? <p className="adm-table__empty">{t('lib_no_elements') || 'Keine Elemente'}</p>
                                    : (
                                        <div className="lib-checklist">
                                            {previewData.elements.map(e => (
                                                <label key={e.id} className={`lib-checkitem ${selectedElems.includes(e.id) ? 'lib-checkitem--selected' : ''}`}>
                                                    <input type="checkbox" checked={selectedElems.includes(e.id)}
                                                        onChange={() => toggleElem(e.id)} />
                                                    <span className="lib-checkitem__name">{e.name || '—'}</span>
                                                    <span className="lib-checkitem__meta">
                                                        {ELEMENT_TYPE_LABELS[e.type] ?? e.type}
                                                        {e.chunk_count > 0 && ` · ${e.chunk_count} Chunks 🧠`}
                                                        {e.file_count > 0  && ` · ${e.file_count} Dateien 📎`}
                                                        {e.status !== 1 && ' · ⏸'}
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    )
                                }
                            </div>

                            <div className="lib-actions">
                                <div className="lib-selection-summary">
                                    {selectedColls.length + selectedElems.length === 0
                                        ? (t('lib_nothing_selected') || 'Nichts ausgewählt')
                                        : `${selectedColls.length} Collection(s) + ${selectedElems.length} Element(e)`}
                                </div>
                                <button className="adm-btn adm-btn--primary" onClick={handleExport}
                                    disabled={exportLoading || (selectedColls.length === 0 && selectedElems.length === 0)}>
                                    {exportLoading ? '⏳ Exportiere…' : '💾 Als .hejvi herunterladen'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── IMPORT ──────────────────────────────────────────────────── */}
            {activeSection === 'import' && (
                <div className="lib-section">
                    <h3 className="adm-section-title">{t('lib_import_title') || 'Paket importieren'}</h3>

                    <div className="lib-row">
                        <div className="lib-field">
                            <label className="adm-label">.hejvi-Datei</label>
                            <input type="file" accept=".hejvi,.json" className="adm-input"
                                onChange={handleFileSelect} />
                        </div>
                    </div>

                    {/* Datei-Info + Versionsvergleich */}
                    {importData && (
                        <div className="lib-import-info">
                            <div className="lib-import-info__header">
                                <span className="lib-import-info__package">
                                    📦 {importData.library_package || 'Unbenannt'} v{importData.library_version || '?'}
                                </span>
                                {versionStatusBadge()}
                                <span className="lib-import-info__meta">
                                    Exportiert: {importData.exported_at
                                        ? new Date(importData.exported_at).toLocaleString('de-DE') : '—'}
                                </span>
                            </div>
                            <div className="lib-import-info__stats">
                                <span>📚 {importData.collections?.length ?? 0} Collections</span>
                                <span>🎬 {importData.elements?.length ?? 0} Elemente</span>
                                <span>🧠 {Object.values(importData.chunks ?? {}).reduce((a, c) => a + c.length, 0)} Chunks</span>
                                <span>📎 {Object.values(importData.file_storage ?? {}).reduce((a, c) => a + c.length, 0)} Dateien</span>
                            </div>
                        </div>
                    )}

                    {/* Ziel + Strategie */}
                    <div className="lib-row">
                        {/* Ziel-Mandant: nur für SuperAdmin wählbar */}
                        {isSuperAdmin && (
                            <div className="lib-field">
                                <label className="adm-label">{t('lib_target_client') || 'Ziel-Mandant'}</label>
                                <select className="adm-input" value={targetClientId}
                                    onChange={e => setTargetClientId(e.target.value)}>
                                    <option value="">— Mandant wählen —</option>
                                    {clientOptions}
                                </select>
                            </div>
                        )}
                        <div className="lib-field">
                            <label className="adm-label">{t('lib_conflict_strategy') || 'Konflikt-Strategie'}</label>
                            <select className="adm-input" value={conflictStrategy}
                                onChange={e => setConflictStrategy(e.target.value)}>
                                {CONFLICT_STRATEGIES_T.map(s => (
                                    <option key={s.value} value={s.value}>
                                        {s.label} — {s.desc}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Strategie-Erklärung */}
                    {conflictStrategy === 'update' && (
                        <p className="lib-hint" style={{ marginBottom: '1rem' }}>
                            <strong>{t('lib_update_mode') || 'Update-Modus'}:</strong> {t('lib_update_desc') || 'Elemente werden nur überschrieben wenn das Paket eine'}
                            neuere library_version hat. Gleiche Version → alles überspringen. Ältere Version → blockiert.
                        </p>
                    )}

                    <div className="lib-actions">
                        <button className="adm-btn adm-btn--primary" onClick={handleImport}
                            disabled={importLoading || !importData || (isSuperAdmin && !targetClientId)}>
                            {importLoading ? '⏳ Importiere…' : '📥 Import starten'}
                        </button>
                    </div>

                    {/* Import-Ergebnis */}
                    {importResult && (
                        <div className={`lib-result ${
                            importResult.version_status === 'downgrade_blocked' ? 'lib-result--blocked' :
                            importResult.version_status === 'up_to_date' ? 'lib-result--current' : ''
                        }`}>
                            {/* Versions-Status Banner */}
                            {importResult.version_status === 'up_to_date' && (
                                <div className="lib-result__banner lib-result__banner--ok">
                                    <div>✅ Paket v{importResult.export_version} ist bereits installiert</div>
                                    <button
                                        className="adm-btn adm-btn--secondary"
                                        style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                                        onClick={() => { setConflictStrategy('overwrite'); setImportResult(null); }}
                                    >
                                        ♻ Trotzdem überschreiben (Overwrite)
                                    </button>
                                </div>
                            )}
                            {importResult.version_status === 'downgrade_blocked' && (
                                <div className="lib-result__banner lib-result__banner--warn">
                                    ⚠ Import blockiert: installierte Version v{importResult.installed_version} ist neuer als v{importResult.export_version}
                                </div>
                            )}
                            {importResult.version_status === 'update' && (
                                <div className="lib-result__banner lib-result__banner--ok">
                                    🔄 Update v{importResult.installed_version} → v{importResult.export_version} durchgeführt
                                </div>
                            )}
                            {importResult.version_status === 'install' && (
                                <div className="lib-result__banner lib-result__banner--ok">
                                    📦 Erstinstallation v{importResult.export_version}
                                </div>
                            )}

                            {/* Stats-Grid */}
                            {importResult.success && importResult.stats && (
                                <div className="lib-result__stats">
                                    {[
                                        ['✅ Elemente importiert',       importResult.stats.elements_imported],
                                        ['♻ Überschrieben',              importResult.stats.elements_overwritten],
                                        ['⏭ Übersprungen',               importResult.stats.elements_skipped],
                                        ['⚠ Doubletten erkannt',         importResult.stats.duplicates_detected || null],
                                        ['📚 Collections importiert',    importResult.stats.collections_imported],
                                        ['⏭ Collections übersprungen',   importResult.stats.collections_skipped],
                                        ['🧠 Chunks',                    importResult.stats.chunks_imported],
                                        ['📂 Dateien kopiert',           importResult.stats.files_copied],
                                        importResult.stats.files_missing > 0
                                            ? ['⚠ Quelldateien fehlend', importResult.stats.files_missing] : null,
                                        ['⚡ Qdrant-Punkte',             importResult.stats.qdrant_synced],
                                        importResult.stats.qdrant_errors > 0
                                            ? ['⚠ Qdrant-Fehler', importResult.stats.qdrant_errors] : null,
                                    ].filter(Boolean).filter(([, v]) => v !== null && v !== 0).map(([label, val]) => (
                                        <div key={label} className="lib-result__stat">
                                            <span className="lib-result__stat-label">{label}</span>
                                            <span className="lib-result__stat-val">{val}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Log */}
                            {importResult.log?.length > 0 && (
                                <details className="lib-result__log-details">
                                    <summary>Import-Log ({importResult.log.length} Einträge)</summary>
                                    <div className="lib-result__log">
                                        {importResult.log.map((line, i) => (
                                            <div key={i} className="lib-result__log-line">{line}</div>
                                        ))}
                                    </div>
                                </details>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Hilfsfunktion: einfacher Versionsvergleich ─────────────────────────────
function compareVersions(a, b) {
    const pa = String(a).split('.').map(Number);
    const pb = String(b).split('.').map(Number);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
        const diff = (pa[i] || 0) - (pb[i] || 0);
        if (diff !== 0) return diff < 0 ? -1 : 1;
    }
    return 0;
}

export default LibraryTab;
